import { useContext, useState } from 'react'
import { SnackbarContext } from 'contexts/SnackbarContext'
import { convertToFullDate } from 'utils/formatUtils'
import { axios } from 'api'
import { useTranslation } from 'react-i18next'

export type PaymentMethod = {
  paymentProfileId: string
  cardNumber: string
  expirationDate: string
  cvv?: string
  cardType: string
  billingAddress: {
    firstName: string
    lastName: string
    address: string
    city: string
    state: string
    zipCode: string
  }
}

export type NewPaymentInfo = {
  cardNumber: string
  expirationDate: string
  cvv: string
  billingAddress: {
    firstName: string
    lastName: string
    address: string
    city: string
    state: string
    zipCode: string
  }
}

export type PaymentInfo = {
  paymentProfileId: string
  expirationDate: string
  billingAddress: {
    firstName: string
    lastName: string
    address: string
    city: string
    state: string
    zipCode: string
  }
}

export type BillingAddressForm = {
  firstName: string
  lastName: string
  addressLine1: string
  addressLine2?: string
  city: string
  state: string
  zipCode: string
}

export type PaymentFormData = {
  paymentProfileId?: string
  cardNumber: string
  expirationDate: string
  cvv: string
  billingAddress: BillingAddressForm
}

export type Address = {
  id: string
  customerName: string
  companyName: string
  phoneNumber: string
  addressLine1: string
  addressLine2?: string
  city: string
  state: string
  zipCode: string
  isPrimaryAddress: boolean
}

export type NewAddress = {
  customerName: string
  companyName: string
  phoneNumber: string
  addressLine1: string
  addressLine2?: string
  city: string
  state: string
  zipCode: string
}

export type Item = {
  productId: string
  quantity: number
  isAssembled: boolean
  hingeDirection: string
}
const usePayment = () => {
  const { t } = useTranslation()
  const { showMessage } = useContext(SnackbarContext)
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])
  const [addresses, setAddresses] = useState<Address[]>([])
  const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] =
    useState<boolean>(true)
  const [isLoadingAddresses, setIsLoadingAddresses] = useState<boolean>(true)

  const fetchPaymentMethods = async () => {
    setIsLoadingPaymentMethods(true)
    const response = await axios.get('/payments/get-payment-methods')
    if (response.status === 200) {
      setPaymentMethods(response.data)
    } else {
      console.error('Failed to fetch payment methods.')
    }
    setIsLoadingPaymentMethods(false)
  }

  const savePaymentInfo = async (newPaymentInfo: NewPaymentInfo) => {
    const response = await axios.post('/payments/save-payment-info', {
      cardNumber: newPaymentInfo.cardNumber,
      expirationDate: convertToFullDate(newPaymentInfo.expirationDate),
      cvv: newPaymentInfo.cvv,
      firstName: newPaymentInfo.billingAddress.firstName,
      lastName: newPaymentInfo.billingAddress.lastName,
      address: newPaymentInfo.billingAddress.address,
      city: newPaymentInfo.billingAddress.city,
      state: newPaymentInfo.billingAddress.state,
      zipCode: newPaymentInfo.billingAddress.zipCode
    })
    if (response.status === 201) {
      const newPaymentMethod = response.data
      setPaymentMethods(prevMethods => [...prevMethods, newPaymentMethod])
      showMessage(t('snackbar.payment.infoSaved'))
    } else {
      const errorMessage =
        response?.data?.error || t('snackbar.general.unexpectedError')
      showMessage(errorMessage, 'error')
    }
  }

  const updatePaymentMethod = async (updatePaymentInfo: PaymentInfo) => {
    const { paymentProfileId, expirationDate, billingAddress } =
      updatePaymentInfo
    const response = await axios.post('/payments/update-payment-method', {
      paymentProfileId,
      expirationDate: convertToFullDate(expirationDate),
      firstName: billingAddress.firstName,
      lastName: billingAddress.lastName,
      address: billingAddress.address,
      city: billingAddress.city,
      state: billingAddress.state,
      zipCode: billingAddress.zipCode
    })
    if (response.status === 200) {
      const updatedPaymentMethod = response.data
      setPaymentMethods(prevMethods =>
        prevMethods.map(method =>
          method.paymentProfileId === updatedPaymentMethod.paymentProfileId
            ? updatedPaymentMethod
            : method
        )
      )
      showMessage(t('snackbar.payment.methodUpdated'))
    } else {
      const errorMessage =
        response?.data?.error || t('snackbar.general.unexpectedError')
      showMessage(errorMessage, 'error')
    }
  }

  const deletePaymentMethod = async (paymentProfileId: string) => {
    const response = await axios.delete(
      `/payments/delete-payment-method/${paymentProfileId}`
    )
    if (response.status === 204) {
      setPaymentMethods(prevMethods =>
        prevMethods.filter(
          method => method.paymentProfileId !== paymentProfileId
        )
      )
      showMessage(t('snackbar.payment.methodDeleted'))
    } else {
      const errorMessage =
        response?.data?.error || t('snackbar.general.unexpectedError')
      showMessage(errorMessage, 'error')
    }
  }

  const addAddress = async (address: NewAddress) => {
    const response = await axios.post('/address-books', address)
    if (response.status === 201) {
      const newAddress: Address = {
        ...response.data.address,
        isPrimaryAddress: true
      }

      setAddresses(prevAddresses =>
        prevAddresses
          .map(addr => ({
            ...addr,
            isPrimaryAddress: false
          }))
          .concat(newAddress)
      )

      showMessage(t('snackbar.address.added'))
    } else {
      const errorMessage =
        response?.data?.error || t('snackbar.general.unexpectedError')
      showMessage(errorMessage, 'error')
    }
  }

  const updateAddress = async (updateAddress: Address) => {
    const response = await axios.put('/address-books', updateAddress)
    if (response.status === 200) {
      const updatedAddress: Address = response.data.address

      if (updatedAddress.isPrimaryAddress) {
        setAddresses(prevAddresses =>
          prevAddresses.map(addr =>
            addr.id === updatedAddress.id
              ? { ...updatedAddress }
              : { ...addr, isPrimaryAddress: false }
          )
        )
      } else {
        setAddresses(prevAddresses =>
          prevAddresses.map(addr =>
            addr.id === updatedAddress.id ? { ...updatedAddress } : addr
          )
        )
      }
      showMessage(t('snackbar.address.updated'))
    } else {
      const errorMessage =
        response?.data?.error || t('snackbar.general.unexpectedError')
      showMessage(errorMessage, 'error')
    }
  }

  const deleteAddress = async (addressId: string) => {
    const response = await axios.delete(`/address-books/${addressId}`)
    if (response.status === 204) {
      setAddresses(prevAddresses =>
        prevAddresses.filter(address => address.id !== addressId)
      )
      showMessage(t('snackbar.address.deleted'))
    } else {
      const errorMessage =
        response?.data?.error || t('snackbar.general.unexpectedError')
      showMessage(errorMessage, 'error')
    }
  }

  const fetchAddresses = async () => {
    setIsLoadingAddresses(true)
    const response = await axios.get('/address-books/all')
    const { success, addresses } = response.data
    if (success) {
      setAddresses(addresses)
    } else {
      setAddresses([])
    }
    setIsLoadingAddresses(false)
  }

  return {
    fetchPaymentMethods,
    savePaymentInfo,
    updatePaymentMethod,
    deletePaymentMethod,
    addAddress,
    updateAddress,
    deleteAddress,
    fetchAddresses,
    paymentMethods,
    addresses,
    isLoadingPaymentMethods,
    isLoadingAddresses
  }
}

export default usePayment
